import { ButtonPrimary, ButtonSecondary } from "components/Atoms/Button";
import React from "react";
import { routes } from "utilities/routing/generatedRoutes";
import { DetailSidePanel } from "../DetailSidePanel";
import { TrainSidePanelDetails } from "./TrainSidePanelDetails";
import { useQueryClient } from "@tanstack/react-query";
import { useOpenSidePanel } from "../SidePanel";
import { RightClickMenu } from "components/RightClickMenu/RightClickMenuContext";
import { rightMenuForSidePanel } from "components/RightClickMenu/rightClickMenuUtils";
import { useOpenNewTrainSidePanel } from "./NewTrainSidePanel";

export interface TrainSidePanelProps {
  trainId: number
  trigger: JSX.Element
}

const AddSegmentButton = ({ trainId }: { trainId: number }) => {
  const openNewTrainSidePanel = useOpenNewTrainSidePanel();

  return <ButtonSecondary label={RESX.Flight.AddSegment} onClick={() => openNewTrainSidePanel(trainId)} />
}

export const TrainSidePanel = (props: TrainSidePanelProps) => {
  const queryClient = useQueryClient();

  return <DetailSidePanel
    detailUrl={routes.train_Detail(props.trainId).absolute}
    apiCall={() => window.CoreApi.Train.GetSidePanelData(props.trainId)}
    apiKey="train"
    entityId={props.trainId}
    title={RESX.Train.resxTrain}
    footerContent={<>
      {site.permissions.train.canCreate && <AddSegmentButton trainId={props.trainId} />}
      <ButtonPrimary href={routes.train_Detail(props.trainId).absolute} label={RESX.Buttons.DetailView} />
    </>}
    trigger={<RightClickMenu
      menuItems={rightMenuForSidePanel(routes.train_Detail(props.trainId).absolute, {
        duplicate: {
          canDuplicate: site.permissions.train.canCreate,
          duplicateFn: async () => {
            const resp = await window.CoreApi.Train.CopyTrain({ trainId: props.trainId });
            return resp.data;
          },
          sidePanelToOpen: "train",
          invalidateCache: () => queryClient.invalidateQueries(["trains"]),
        },
      })}
    >
      {props.trigger}
    </RightClickMenu>}
  >
    {(data, refetch) => <TrainSidePanelDetails
      trainSidePanelData={data}
      refetch={refetch}
    />}
  </DetailSidePanel>
};

export const useOpenTrainSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openTrainSidePanel = React.useCallback((trainId: number) => {
    openSidePanel(<DetailSidePanel
      apiCall={() => window.CoreApi.Train.GetSidePanelData(trainId)}
      apiKey="train"
      entityId={trainId}
      title={RESX.Train.resxTrain}
      footerContent={<>
        {site.permissions.train.canCreate && <AddSegmentButton trainId={trainId} />}
        <ButtonPrimary href={routes.train_Detail(trainId).absolute} label={RESX.Buttons.DetailView} />
      </>}
      skipSidePanelBase
    >
      {(data, refetch) => <TrainSidePanelDetails
        trainSidePanelData={data}
        refetch={refetch}
      />}
    </DetailSidePanel>, `train-side-${trainId}`);

  }, [openSidePanel]);

  return openTrainSidePanel;
}
